import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Menu, Power, AlertTriangle } from 'lucide-react'
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Eye,
  ArrowUpDown,
  FolderTree,
  ExternalLink
} from 'lucide-react'
import MenuFormModal from '../../components/modals/MenuFormModal'
import MenuOrderModal from '../../components/modals/MenuOrderModal'
import { menuService, MenuItem } from '../../services/menuService'
import toast from 'react-hot-toast'
import TestMenus from '../../components/TestMenus'

const MenusPage: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showOrderModal, setShowOrderModal] = useState(false)
  const [editMenu, setEditMenu] = useState<MenuItem | null>(null)
  const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
  const queryClient = useQueryClient()

  // Cargar menús
  const { data: menus, isLoading, error, refetch } = useQuery({
    queryKey: ['menus-test-data', 'v3'], // Cambiar clave para forzar nueva query
    queryFn: async () => {
      console.log('React Query: Starting query...')
      try {
        const result = await menuService.getMenus()
        console.log('React Query: Query successful:', result?.length, 'menus')
        return result
      } catch (err) {
        console.error('React Query: Query failed:', err)
        throw err
      }
    },
    staleTime: 0, // No usar cache
    cacheTime: 0, // No almacenar en cache
    retry: false  // No reintentar para debugging
  })

  // Debug logging
  useEffect(() => {
    console.log('MenusPage Debug:', {
      menus: menus?.length || 'undefined',
      isLoading,
      error: error && (error as any).message ? (error as any).message : 'none'
    })
  }, [menus, isLoading, error])

  // Expandir automáticamente todos los menús con hijos cuando se cargan los datos
  useEffect(() => {
    if (menus && menus.length > 0) {
      const getAllMenuIdsWithChildren = (items: MenuItem[]): number[] => {
        let ids: number[] = []
        for (const item of items) {
          if (item.children && item.children.length > 0) {
            ids.push(item.id)
            ids = ids.concat(getAllMenuIdsWithChildren(item.children))
          }
        }
        return ids
      }
      
      const idsToExpand = getAllMenuIdsWithChildren(menus)
      setExpandedItems(new Set(idsToExpand))
    }
  }, [menus])

  // Cuando se cierra cualquier modal, refrescar la lista de menús
  useEffect(() => {
    if (!showCreateModal && !showOrderModal && !selectedMenu) {
      refetch()
    }
  }, [showCreateModal, showOrderModal, selectedMenu, refetch])

  // Eliminar menú
  const deleteMenuMutation = useMutation({
    mutationFn: (id: number) => menuService.deleteMenu(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['menus-test-data'])
      queryClient.invalidateQueries(['user-menus-sidebar'])
      toast.success('Menú eliminado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al eliminar menú')
    }
  })

  // Cambiar estado (activar/desactivar) menú
  const toggleMenuStatusMutation = useMutation({
    mutationFn: (id: number) => menuService.toggleMenuStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['menus-test-data'])
      queryClient.invalidateQueries(['user-menus-sidebar'])
      toast.success('Estado del menú actualizado')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al cambiar el estado del menú')
    }
  })

  const handleDeleteMenu = (menu: MenuItem) => {
    if (window.confirm(`¿Estás seguro de eliminar el menú ${menu.name}?`)) {
      deleteMenuMutation.mutate(menu.id)
    }
  }
  
  const handleToggleStatus = (menu: MenuItem) => {
    toggleMenuStatusMutation.mutate(menu.id)
  }

  const toggleExpanded = (menuId: number) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId)
    } else {
      newExpanded.add(menuId)
    }
    setExpandedItems(newExpanded)
  }

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.has(item.id)

    return (
      <div key={item.id}>
        <div 
          className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors ${
            level > 0 ? 'ml-8 mt-2' : 'mb-2'
          }`}
        >
          <div className="flex items-center flex-1">
            {hasChildren && (
              <button
                onClick={() => toggleExpanded(item.id)}
                className="mr-2 p-1 hover:bg-gray-200 rounded"
              >
                <FolderTree className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </button>
            )}
            
            <div className="flex items-center">
              <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                <Menu className="h-5 w-5 text-primary-600" />
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900">{item.name}</h3>
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <span>{item.path || 'Sin ruta'}</span>
                  {item.path && (
                    <ExternalLink className="h-3 w-3 ml-1" />
                  )}
                </div>
                <div className="flex items-center mt-1">
                  <span className="text-xs text-gray-400 mr-3">
                    Orden: {item.sort_order}
                  </span>
                  <div className="flex gap-1">
                    {(item.roles || []).map((role) => (
                      <span
                        key={role}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleToggleStatus(item)}
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                item.is_active
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-red-100 text-red-800 hover:bg-red-200'
              }`}
              title={item.is_active ? 'Desactivar' : 'Activar'}
            >
              <Power className="h-3 w-3 mr-1" />
              {item.is_active ? 'Activo' : 'Inactivo'}
            </button>

            <button
              onClick={() => setSelectedMenu(item)}
              className="text-blue-600 hover:text-blue-900 p-1"
              title="Ver detalles"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => setEditMenu(item)}
              className="text-gray-600 hover:text-gray-900 p-1"
              title="Editar"
            >
              <Edit3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowOrderModal(true)}
              className="text-gray-600 hover:text-gray-900 p-1"
              title="Reordenar"
            >
              <ArrowUpDown className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDeleteMenu(item)}
              className="text-red-600 hover:text-red-900 p-1"
              title="Eliminar"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="mt-2">
            {item.children?.map((child) => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error al cargar menús</h2>
          <p className="text-gray-600 mb-4">No se pudieron cargar los menús. Por favor, inténtalo nuevamente.</p>
          <button 
            onClick={() => refetch()}
            className="btn btn-primary-static btn-md"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Test Component - TEMPORARY */}
      <TestMenus />
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Menús</h1>
          <p className="mt-2 text-sm text-gray-600">
            Administra la estructura de navegación del sistema
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary-static btn-md"
        >
          <Plus className="h-5 w-5" />
          Nuevo Menú
        </button>
      </div>

      {/* Menus List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : menus && menus.length > 0 ? (
          menus.map((menu) => renderMenuItem(menu))
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No hay menús disponibles</p>
          </div>
        )}
      </div>

      {/* Menu Details Modal */}
      {selectedMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-96 overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Detalles del Menú: {selectedMenu.name}</h2>
            
            <div className="space-y-4">
              <div>
                <span className="font-medium">Nombre:</span> {selectedMenu.name}
              </div>
              
              <div>
                <span className="font-medium">Ruta:</span> {selectedMenu.path || 'Sin ruta definida'}
              </div>

              <div>
                <span className="font-medium">Ícono:</span> {selectedMenu.icon || 'Sin ícono'}
              </div>

              <div>
                <span className="font-medium">Orden:</span> {selectedMenu.sort_order}
              </div>

              <div>
                <span className="font-medium">Estado:</span> {selectedMenu.is_active ? 'Activo' : 'Inactivo'}
              </div>

              <div>
                <span className="font-medium">Roles con acceso:</span>
                <div className="mt-1 flex gap-2">
                  {(selectedMenu.roles || []).map((role) => (
                    <span
                      key={role}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>

              {selectedMenu.parent_id && (
                <div>
                  <span className="font-medium">Menú padre:</span> {selectedMenu.parent_id}
                </div>
              )}

              {selectedMenu.children && selectedMenu.children.length > 0 && (
                <div>
                  <span className="font-medium">Submenús:</span>
                  <ul className="mt-1 list-disc list-inside">
                    {selectedMenu.children.map((child) => (
                      <li key={child.id} className="text-sm text-gray-600">
                        {child.name} ({child.path})
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <span className="font-medium">Fecha de creación:</span> {selectedMenu.created_at ? new Date(selectedMenu.created_at as any).toLocaleString() : '-'}
              </div>
              
              <div>
                <span className="font-medium">Última actualización:</span> {selectedMenu.updated_at ? new Date(selectedMenu.updated_at as any).toLocaleString() : '-'}
              </div>
            </div>

            <button
              onClick={() => setSelectedMenu(null)}
              className="btn btn-secondary w-full mt-6"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <MenuFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          menu={null}
        />
      )}

      {editMenu && (
        <MenuFormModal
          isOpen={!!editMenu}
          onClose={() => setEditMenu(null)}
          menu={editMenu}
        />
      )}

      {showOrderModal && menus && (
        <MenuOrderModal
          isOpen={showOrderModal}
          onClose={() => setShowOrderModal(false)}
          menus={menus}
        />
      )}
    </div>
  )
}

export default MenusPage
